import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  untracked,
  inject,
  ViewChild,
} from '@angular/core';
import { Subject, forkJoin, from, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import {
  FormField,
  ActionButton,
  SectionButton,
  AddFormComponent,
} from '@shared/components/add-form/add-form.component';
import {
  SynchronizationFormConfig,
  SYNCHRONIZATION_SECTION_BUTTONS,
} from '../../synchronization.config';
import { SynchronizationService } from '../../services/synchronization.service';
import {
  SelectionDialogComponent,
  SelectionItem,
} from '../shared/selection-dialog/selection-dialog.component';
import {
  WAREHOUSE_SELECTION_CONFIG,
  PRODUCT_SELECTION_CONFIG,
} from '../shared/selection-dialog/selection-dialog.config';
import {
  Warehouse,
  Product,
  SynchronizationDetail,
  WarehouseSearchParams,
  ProductSearchParams,
} from '../../models/synchronization.model';
import { DataService } from '../../services/data.service';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  extractErrorMessage,
  getErrorSeverity,
  getErrorTitle,
} from '@shared/utils/error-handler.util';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  FefoOverrideDialogComponent,
  FefoOverrideDialogData,
  FefoOverrideDialogResult,
} from '../../../shared/fefo-override-dialog/fefo-override-dialog.component';
import {
  fefoSuggestedBatchId,
  isLotRequiredFromResolve,
  needsFefoOverrideReason,
} from '../../../shared/fefo-lot.util';

@Component({
  selector: 'app-synchronization-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ToastModule,
    AddFormComponent,
    SelectionDialogComponent,
    ConfirmationDialogComponent,
    FefoOverrideDialogComponent,
  ],
  providers: [SynchronizationService],
  templateUrl: './synchronization-form.component.html',
  styleUrls: ['./synchronization-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SynchronizationFormComponent
  implements OnInit, OnDestroy, OnChanges
{
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private readonly batchManagementService = inject(BatchManagementService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() config!: SynchronizationFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @Input() initialData: any = null;
  @Input() height: string = '100%';
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;
  // Form management
  form!: FormGroup;

  // Form data management
  formData = signal<any>({});
  loading = signal(false);
  private skipCanDeactivate = false;

  // Section buttons configuration - empty for view mode
  sectionButtons: SectionButton[] = [];

  // Table data management
  productTableData = signal<SynchronizationDetail[]>([]);
  selectedProducts = signal<SynchronizationDetail[]>([]);

  // Dialog state
  showSelectionDialog = signal(false);
  currentDialogConfig = signal<any>(null);

  fefoOverrideVisible = false;
  fefoOverrideData: FefoOverrideDialogData | null = null;
  private pendingFefoLines: SynchronizationDetail[] = [];
  private pendingSavePayload: any = null;

  // Dialog data state
  dialogItems = signal<SelectionItem[]>([]);
  dialogLoading = signal(false);
  dialogTotalRecords = signal(0);
  dialogSearchParams = signal<Record<string, string>>({});
  dialogCurrentPage = signal(1);
  dialogInitialSelection = signal<SelectionItem[]>([]);
  dialogDisabledSelection = signal<SelectionItem[]>([]);

  // Validation state
  validationErrors = signal<string[]>([]);
  fieldValidationErrors = signal<Record<string, string>>({});

  // Confirmation dialog state
  showDeleteConfirmationDialog = false;
  deleteConfirmationTitle = '';
  deleteConfirmationMessage = '';
  deleteConfirmationButtons: ConfirmationButton[] = [];

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const formDataValue = this.formData();
    const baseData = {
      ...formDataValue,
    };

    return baseData;
  });

  // Computed property to get updated table sections with current data
  updatedTableSections = computed(() => {
    if (!this.config.tableSections) return [];

    return this.config.tableSections.map((section) => {
      if (section.title === 'synchronization.form.sections.product_details') {
        return {
          ...section,
          data: this.productTableData(),
          selection: this.selectedProducts(),
          actions: section.actions?.map((action: any) => {
            if (action.action === 'deleteSelected') {
              return {
                ...action,
                disabled: this.selectedProducts().length === 0,
              };
            }
            return action;
          }),
        };
      }
      return section;
    });
  });

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly synchronizationService: SynchronizationService,
    private readonly translateService: TranslateService,
    private readonly dataService: DataService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.handleInitialData();
    this.setSectionButtons();
  }

  private initializeForm(): void {
    const formControls: any = {};

    // Initialize form controls for all fields
    this.config.formFields.forEach((field) => {
      formControls[field.controlName] = [
        field.defaultValue || '',
        field.validators || [],
      ];
    });

    this.form = this.fb.group(formControls);

    // Subscribe to form value changes
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.formData.set(value);
    });
  }

  private setSectionButtons(): void {
    // Only show section buttons in edit mode, not in view mode
    if (!this.viewMode) {
      this.sectionButtons = SYNCHRONIZATION_SECTION_BUTTONS;
    } else {
      this.sectionButtons = [];
    }
  }

  private handleInitialData(): void {
    if ((this.editMode || this.viewMode) && this.initialData) {
      // Set form data
      this.formData.set({ ...this.initialData });

      // Patch form controls with initial data
      this.form.patchValue({
        adjustCode: this.initialData.adjustCode,
        warehouseId: this.initialData.warehouseId,
        warehouseCode: this.initialData.warehouseCode,
        warehouseName: this.initialData.warehouseName,
        remarks: this.initialData.remarks,
      });

      // Map adjustDetailResVOList to productTableData if it exists
      if (
        this.initialData.adjustDetailsReqDTOList &&
        Array.isArray(this.initialData.adjustDetailsReqDTOList)
      ) {
        const mappedProducts: SynchronizationDetail[] =
          this.initialData.adjustDetailsReqDTOList.map(
            (item: any, index: number) => ({
              id: item.id || index + 1,
              serialNumber: item.serialNumber || index + 1,
              categoryName: item.categoryName || '',
              spuCode: item.spuCode || '',
              spuName: item.spuName || '',
              skuCode: item.skuCode || '',
              skuName: item.skuName || '',
              adjustmentType:
                item.type === 1
                  ? 'INCREASE'
                  : item.type === 0
                    ? 'DECREASE'
                    : item.adjustmentType,
              adjustmentQuantity: item.num || item.planNum || null,
              planNum: item.planNum || null,
              realityNum: item.realityNum || null,
              cancelNum: item.cancelNum || 0,
            }),
          );
        this.productTableData.set(mappedProducts);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle initialData changes
    if (
      changes['initialData'] &&
      (this.editMode || this.viewMode) &&
      this.initialData
    ) {
      // Create a copy of the initial data
      const formDataCopy = { ...this.initialData };
      this.formData.set(formDataCopy);

      // Patch form controls with initial data including adjustCode
      // this.form.patchValue({
      //   adjustCode: this.initialData.adjustCode,
      //   warehouseId: this.initialData.warehouseId,
      //   warehouseCode: this.initialData.warehouseCode,
      //   warehouseName: this.initialData.warehouseName,
      //   remark: this.initialData.remark
      // });

      // Map adjustDetailResVOList to productTableData if it exists
      if (
        this.initialData.adjustDetailsReqDTOList &&
        Array.isArray(this.initialData.adjustDetailsReqDTOList)
      ) {
        const mappedProducts: SynchronizationDetail[] =
          this.initialData.adjustDetailsReqDTOList.map(
            (item: any, index: number) => ({
              id: item.id || index + 1,
              serialNumber: item.serialNumber || index + 1,
              categoryName: item.categoryName || '',
              spuCode: item.spuCode || '',
              spuName: item.spuName || '',
              skuCode: item.skuCode || '',
              skuName: item.skuName || '',
              adjustmentType:
                item.type === 1
                  ? 'INCREASE'
                  : item.type === 0
                    ? 'DECREASE'
                    : item.adjustmentType,
              adjustmentQuantity: item.num || null,
            }),
          );
        this.productTableData.set(mappedProducts);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormValueChanges(value: any): void {
    this.formData.set(value);
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save' && event.data) {
      // Validate before saving
      if (this.validateBeforeSave()) {
        this.beginSaveWithFefoCheck(event.data);
      }
    } else if (event.action === 'cancel') {
      this.router.navigate(['/stock/synchronization']);
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return Boolean(
      !(this.addFormComponent?.form?.dirty || this.form?.dirty)
    );
  }

  onSectionAction(event: {
    section: string;
    action: string;
    data?: any;
  }): void {
    switch (event.action) {
      case 'selectWarehouse':
        this.openWarehouseSelectionDialog();
        break;
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    if (event.action === 'selectionChange') {
      // Handle selection change
      this.selectedProducts.set(event.data || []);
    } else if (event.action === 'deleteSelected') {
      // Handle delete selected products
      this.onDeleteSelectedProducts();
    } else if (event.action === 'selectProducts') {
      // Handle select products action
      this.openProductSelectionDialog();
    } else if (event.action === 'cellValueChange') {
      // Handle cell value changes (adjustment type, quantity)
      this.onCellValueChange(event.data);
    }
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    // Update the product table data
    const updatedData = this.productTableData().map((product) => {
      if (product.id === event.rowData.id) {
        const updated: SynchronizationDetail = {
          ...product,
          [event.prop]: event.value,
        };
        if (
          event.prop === 'batchId' ||
          event.prop === 'adjustmentQuantity' ||
          event.prop === 'adjustmentType'
        ) {
          updated.overrideReason = undefined;
          updated.fefoSuggestedBatchId = undefined;
        }
        return updated;
      }
      return product;
    });
    this.productTableData.set(updatedData);

    // Clear field validation error for this specific field
    const productIndex = this.productTableData().findIndex(
      (p) => p.id === event.rowData.id,
    );
    if (productIndex !== -1) {
      const fieldKey = `${productIndex}-${event.prop}`;
      const currentErrors = this.fieldValidationErrors();
      if (currentErrors[fieldKey]) {
        const newErrors = { ...currentErrors };
        delete newErrors[fieldKey];
        this.fieldValidationErrors.set(newErrors);
      }
    }
  }

  onFefoOverrideResult(result: FefoOverrideDialogResult): void {
    const current = this.pendingFefoLines[0];
    if (!result.confirmed || !result.reason?.trim()) {
      this.pendingFefoLines = [];
      this.pendingSavePayload = null;
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'outbound_management.messages.fefo_override_required',
        ),
      });
      this.cdr.markForCheck();
      return;
    }
    if (current) {
      const products = this.productTableData().map((product) =>
        product.id === current.id || product.skuCode === current.skuCode
          ? { ...product, overrideReason: result.reason!.trim() }
          : product,
      );
      this.productTableData.set(products);
    }
    this.pendingFefoLines = this.pendingFefoLines.slice(1);
    if (this.pendingFefoLines.length > 0) {
      this.openNextFefoOverrideDialog();
      return;
    }
    const payload = this.pendingSavePayload;
    this.pendingSavePayload = null;
    if (payload) {
      this.loading.set(true);
      this.persistSynchronization(payload);
    }
  }

  private openNextFefoOverrideDialog(): void {
    const line = this.pendingFefoLines[0];
    if (!line) {
      return;
    }
    this.fefoOverrideData = {
      skuCode: line.skuCode,
      chosenLot: line.batchId,
      suggestedLot: line.fefoSuggestedBatchId,
    };
    this.fefoOverrideVisible = true;
    this.cdr.markForCheck();
  }

  // Method to get field validation error
  getFieldError(rowIndex: number, fieldName: string): string | null {
    const fieldKey = `${rowIndex}-${fieldName}`;
    return this.fieldValidationErrors()[fieldKey] || null;
  }

  onDeleteSelectedProducts(): void {
    const selectedCount = this.selectedProducts().length;
    if (selectedCount === 0) {
      return;
    }

    // Show confirmation dialog
    this.deleteConfirmationTitle = this.translateService.instant(
      'synchronization.dialog.confirm_delete_products.title',
    );
    this.deleteConfirmationMessage = this.translateService.instant(
      'synchronization.dialog.confirm_delete_products.message',
      { count: selectedCount },
    );
    this.deleteConfirmationButtons = [
      {
        label: this.translateService.instant(
          'synchronization.dialog.actions.cancel',
        ),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          'synchronization.dialog.actions.remove',
        ),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showDeleteConfirmationDialog = true;
  }

  // Handle confirmation dialog button clicks
  onDeleteConfirmatiButtonClick(action: string): void {
    if (action === 'confirm') {
      this.confirmDeleteSelectedProducts();
    }
    this.closeDeleteConfirmationDialog();
  }

  // Actually delete the selected products
  private confirmDeleteSelectedProducts(): void {
    const selectedIds = this.selectedProducts().map((product) => product.id);
    const filteredData = this.productTableData().filter(
      (product) => !selectedIds.includes(product.id),
    );
    this.productTableData.set(filteredData);
    this.selectedProducts.set([]); // Clear selection after deletion

    // Show success toast
    const deletedCount = selectedIds.length;
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant(
        'synchronization.messages.success.title',
      ),
      detail: this.translateService.instant(
        'synchronization.messages.success.products_deleted',
        { count: deletedCount },
      ),
      life: 3000,
    });
  }

  // Close the confirmation dialog
  private closeDeleteConfirmationDialog(): void {
    this.showDeleteConfirmationDialog = false;
    this.deleteConfirmationTitle = '';
    this.deleteConfirmationMessage = '';
    this.deleteConfirmationButtons = [];
  }

  // Validation method to ensure warehouse and products are selected
  private validateBeforeSave(): boolean {
    // Check both formData signal and form.value to ensure we have the latest data
    const formData = this.formData();
    const formValue = this.form.value;
    const hasWarehouse =
      (formData.warehouseCode && formData.warehouseName) ||
      (formValue.warehouseCode && formValue.warehouseName);
    const hasProducts = this.productTableData().length > 0;

    const fieldErrors: Record<string, string> = {};

    // Validate warehouse
    if (!hasWarehouse) {
      const message = this.translateService.instant(
        'synchronization.validation.warehouse_required',
      );
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'synchronization.messages.error.title',
        ),
        detail: message,
        life: 5000,
      });
      return false;
    }

    // Validate products
    if (!hasProducts) {
      const message = this.translateService.instant(
        'synchronization.validation.products_required',
      );
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'synchronization.messages.error.title',
        ),
        detail: message,
        life: 5000,
      });
      return false;
    }

    // Validate individual product fields
    this.productTableData().forEach((product, index) => {
      if (!product.adjustmentType) {
        fieldErrors[`${index}-adjustmentType`] = this.translateService.instant(
          'synchronization.validation.adjustment_type_required',
        );
      }

      if (!product.adjustmentQuantity || product.adjustmentQuantity <= 0) {
        fieldErrors[`${index}-adjustmentQuantity`] =
          this.translateService.instant(
            'synchronization.validation.adjustment_quantity_required',
          );
      }
    });

    // Set field validation errors
    this.fieldValidationErrors.set(fieldErrors);

    // If there are field errors, don't save
    if (Object.keys(fieldErrors).length > 0) {
      return false;
    }

    // Clear validation errors if validation passes
    this.fieldValidationErrors.set({});
    return true;
  }

  // Helper method to clear specific validation errors
  private clearValidationError(type: 'warehouse' | 'products'): void {
    const currentErrors = this.validationErrors();
    const filteredErrors = currentErrors.filter((error) => {
      if (type === 'warehouse') {
        return !error.includes('warehouse');
      } else if (type === 'products') {
        return !error.includes('product');
      }
      return true;
    });
    this.validationErrors.set(filteredErrors);
  }

  // Open warehouse selection dialog - show dialog first, then load data
  private openWarehouseSelectionDialog(): void {
    this.currentDialogConfig.set(WAREHOUSE_SELECTION_CONFIG);
    this.dialogSearchParams.set({});
    this.dialogCurrentPage.set(1);

    // Set initial selection if warehouse is already selected
    // Check both formData signal and form.value to ensure we have the latest data
    const formData = this.formData();
    const formValue = this.form.value;
    const warehouseCode = formData.warehouseCode || formValue.warehouseCode;
    const warehouseName = formData.warehouseName || formValue.warehouseName;
    const warehouseId = formData.warehouseId || formValue.warehouseId;

    if (warehouseCode && warehouseName) {
      const currentWarehouse: Warehouse = {
        id: warehouseId || 0, // Use 0 as fallback if warehouseId is undefined
        code: warehouseCode,
        name: warehouseName,
        type: formData.warehouseType || formValue.warehouseType || '',
        status: formData.warehouseStatus || formValue.warehouseStatus || 1,
        address: formData.warehouseAddress || formValue.warehouseAddress || '',
        remarks: formData.warehouseRemark || formValue.warehouseRemark || '',
      };
      this.dialogInitialSelection.set([currentWarehouse]);
    } else {
      this.dialogInitialSelection.set([]);
    }

    // Show dialog immediately
    this.showSelectionDialog.set(true);

    // Load data with loading state
    this.loadWarehouseData();
  }

  // Open product selection dialog - show dialog first, then load data
  private openProductSelectionDialog(): void {
    this.currentDialogConfig.set(PRODUCT_SELECTION_CONFIG);
    this.dialogSearchParams.set({});
    this.dialogCurrentPage.set(1);

    // Set disabled selection for already selected products
    const currentProducts = this.productTableData();
    if (currentProducts.length > 0) {
      // Convert SynchronizationDetail to Product for disabled selection
      const disabledProducts: Product[] = currentProducts.map((detail) => ({
        id: detail.id || 0,
        code: detail.spuCode,
        name: detail.spuName,
        categoryName: detail.categoryName,
        skuCode: detail.skuCode,
        skuName: detail.skuName || '',
        spuCode: detail.spuCode,
        spuName: detail.spuName,
        productType: '',
      }));
      this.dialogDisabledSelection.set(disabledProducts);
      this.dialogInitialSelection.set(disabledProducts); // Show as checked but disabled
    } else {
      this.dialogDisabledSelection.set([]);
      this.dialogInitialSelection.set([]);
    }

    // Show dialog immediately
    this.showSelectionDialog.set(true);

    // Load data with loading state
    this.loadProductData();
  }

  // Load warehouse data
  private loadWarehouseData(): void {
    this.dialogLoading.set(true);

    const searchParams: WarehouseSearchParams = {
      code: this.dialogSearchParams()['code'] || undefined,
      name: this.dialogSearchParams()['name'] || undefined,
      status: this.dialogSearchParams()['status']
        ? parseInt(this.dialogSearchParams()['status'])
        : undefined,
      page: this.dialogCurrentPage() - 1,
      size: 20,
    };

    this.dataService.getWarehouses(searchParams).subscribe({
      next: (response) => {
        this.dialogItems.set(response.content);
        this.dialogTotalRecords.set(response.totalElements);
        this.dialogLoading.set(false);
      },
      error: (error) => {
        console.error('Form Component: Error loading warehouses:', error);
        this.dialogItems.set([]);
        this.dialogTotalRecords.set(0);
        this.dialogLoading.set(false);

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'synchronization.messages.error.title',
          ),
          detail: this.translateService.instant(
            'synchronization.messages.error.failed_to_load_warehouses',
          ),
          life: 5000,
        });
      },
    });
  }

  // Load product data
  private loadProductData(): void {
    this.dialogLoading.set(true);

    const searchParams: ProductSearchParams = {
      productName: this.dialogSearchParams()['productName'],
      productCode: this.dialogSearchParams()['productCode'],
      skuCode: this.dialogSearchParams()['skuCode'],
      page: this.dialogCurrentPage(),
      size: 20,
    };

    this.dataService.getProducts(searchParams).subscribe({
      next: (response) => {
        this.dialogItems.set(response.content);
        this.dialogTotalRecords.set(response.totalElements);
        this.dialogLoading.set(false);
      },
      error: (error) => {
        console.error('Form Component: Error loading products:', error);
        this.dialogItems.set([]);
        this.dialogTotalRecords.set(0);
        this.dialogLoading.set(false);

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'synchronization.messages.error.title',
          ),
          detail: this.translateService.instant(
            'synchronization.messages.error.failed_to_load_products',
          ),
          life: 5000,
        });
      },
    });
  }

  // Dialog event handlers
  onDialogSearch(searchParams: Record<string, string>): void {
    this.dialogSearchParams.set(searchParams);
    this.dialogCurrentPage.set(1);

    const config = this.currentDialogConfig();
    if (config?.type === 'warehouse') {
      this.loadWarehouseData();
    } else if (config?.type === 'product') {
      this.loadProductData();
    }
  }

  onDialogSearchReset(): void {
    this.dialogSearchParams.set({});
    this.dialogCurrentPage.set(1);

    const config = this.currentDialogConfig();
    if (config?.type === 'warehouse') {
      this.loadWarehouseData();
    } else if (config?.type === 'product') {
      this.loadProductData();
    }
  }

  onDialogPageChange(page: number): void {
    this.dialogCurrentPage.set(page);

    const config = this.currentDialogConfig();
    if (config?.type === 'warehouse') {
      this.loadWarehouseData();
    } else if (config?.type === 'product') {
      this.loadProductData();
    }
  }

  // Unified selection dialog handler
  onSelectionConfirm(selectedItems: SelectionItem[]): void {
    const config = this.currentDialogConfig();

    if (config?.type === 'warehouse' && selectedItems.length > 0) {
      const selectedWarehouse = selectedItems[0] as Warehouse;

      // Update form control
      this.form.patchValue({
        warehouseId: selectedWarehouse.id,
        warehouseCode: selectedWarehouse.code,
        warehouseName: selectedWarehouse.name,
      });

      // Explicitly update formData signal to ensure it's synced with add-form component
      const currentFormData = this.formData();
      this.formData.set({
        ...currentFormData,
        warehouseId: selectedWarehouse.id,
        warehouseCode: selectedWarehouse.code,
        warehouseName: selectedWarehouse.name,
      });

      // Clear warehouse validation error
      this.clearValidationError('warehouse');

      // Show success toast
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant(
          'synchronization.messages.success.title',
        ),
        detail: this.translateService.instant(
          'synchronization.messages.success.warehouse_selected',
          { name: selectedWarehouse.name },
        ),
        life: 3000,
      });
    } else if (config?.type === 'product' && selectedItems.length > 0) {
      const selectedProducts = selectedItems as Product[];
      const disabledProducts = this.dialogDisabledSelection();

      // Filter out products that are already in the table (disabled selection)
      const newProducts = selectedProducts.filter(
        (product) =>
          !disabledProducts.some((disabled) => disabled.id === product.id),
      );

      if (newProducts.length > 0) {
        // Convert new products to synchronization details
        const newProductDetails = newProducts.map((product, index) => ({
          id: product.id,
          serialNumber: this.productTableData().length + index + 1,
          categoryName: product.categoryName,
          spuCode: product.spuCode,
          spuName: product.spuName,
          skuCode: product.skuCode,
          skuName: product.skuName,
          adjustmentType: undefined,
          adjustmentQuantity: null,
        }));

        // Add only new products to product table data
        this.productTableData.set([
          ...this.productTableData(),
          ...newProductDetails,
        ]);

        // Clear products validation error
        this.clearValidationError('products');

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant(
            'synchronization.messages.success.title',
          ),
          detail: this.translateService.instant(
            'synchronization.messages.success.products_selected',
            { count: newProducts.length },
          ),
          life: 3000,
        });
      }
    }

    this.showSelectionDialog.set(false);
  }

  private beginSaveWithFefoCheck(synchronizationData: any): void {
    const latestFormData = this.formData();
    const formValue = this.form.value;
    const warehouseId =
      latestFormData.warehouseId ??
      formValue.warehouseId ??
      synchronizationData.warehouseId;

    this.pendingSavePayload = synchronizationData;

    const decreaseWithLot = this.productTableData().filter(
      (product) =>
        product.adjustmentType === 'DECREASE' && !!product.batchId?.trim(),
    );

    if (!warehouseId || decreaseWithLot.length === 0) {
      this.persistSynchronization(synchronizationData);
      return;
    }

    this.loading.set(true);
    forkJoin(
      decreaseWithLot.map((product) =>
        this.batchManagementService
          .resolveSale({
            skuCode: product.skuCode,
            warehouseId: Number(warehouseId),
            quantity:
              product.adjustmentQuantity && product.adjustmentQuantity > 0
                ? product.adjustmentQuantity
                : 1,
            suggestOnly: true,
          })
          .pipe(
            switchMap((response: any) =>
              BlobConverterUtil.isBlob(response)
                ? from(BlobConverterUtil.convertToJson(response))
                : of(response),
            ),
            catchError(() => of(null)),
            map((result) => ({
              key: `${product.id ?? ''}:${product.skuCode}`,
              result,
            })),
          ),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          const suggestionByKey = new Map(
            rows.map((row) => [row.key, row.result] as const),
          );
          const updated = this.productTableData().map((product) => {
            const key = `${product.id ?? ''}:${product.skuCode}`;
            const result = suggestionByKey.get(key);
            if (!result) {
              return product;
            }
            return {
              ...product,
              lotRequired: isLotRequiredFromResolve(result),
              fefoSuggestedBatchId: fefoSuggestedBatchId(result),
            };
          });
          this.productTableData.set(updated);

          this.pendingFefoLines = updated.filter(
            (product) =>
              product.adjustmentType === 'DECREASE' &&
              needsFefoOverrideReason({
                lotRequired: product.lotRequired,
                chosenLot: product.batchId,
                suggestedLot: product.fefoSuggestedBatchId,
                overrideReason: product.overrideReason,
              }),
          );

          if (this.pendingFefoLines.length > 0) {
            this.loading.set(false);
            this.openNextFefoOverrideDialog();
            return;
          }
          this.persistSynchronization(synchronizationData);
        },
        error: () => {
          this.loading.set(false);
          this.pendingSavePayload = null;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'synchronization.messages.error.title',
            ),
            detail: this.translateService.instant(
              'outbound_management.messages.fefo_override_required',
            ),
          });
        },
      });
  }

  private persistSynchronization(synchronizationData: any): void {
    this.loading.set(true);
    const syncId = this.route.snapshot.queryParams['id'];

    // Get the latest formData to ensure warehouse info is included
    const latestFormData = this.formData();
    const formValue = this.form.value;

    // Prepare the data for submission - merge formData signal with event data to ensure all fields are included
    // Priority: formData signal > form.value > event.data
    // IMPORTANT: Don't spread synchronizationData first as it may contain undefined values that overwrite good data
    const submitData = {
      // Start with event data (from add-form component)
      ...synchronizationData,
      // Override with formData signal (most reliable source)
      ...latestFormData,
      // Explicitly set warehouse info with priority: formData > form.value > event.data
      warehouseId:
        latestFormData.warehouseId ??
        formValue.warehouseId ??
        synchronizationData.warehouseId,
      warehouseCode:
        latestFormData.warehouseCode ??
        formValue.warehouseCode ??
        synchronizationData.warehouseCode,
      warehouseName:
        latestFormData.warehouseName ??
        formValue.warehouseName ??
        synchronizationData.warehouseName,
      remarks:
        latestFormData.remarks ??
        formValue.remarks ??
        synchronizationData.remarks ??
        '',
      // Override adjustDetailsReqDTOList with current product table data
      adjustDetailsReqDTOList: this.productTableData()
        .filter((product) => product.adjustmentType) // Only include products with selected adjustment type
        .map((product) => ({
          num: product.adjustmentQuantity,
          skuCode: product.skuCode,
          type: product.adjustmentType === 'INCREASE' ? 1 : 0,
          batchId: product.batchId || undefined,
          overrideReason: product.overrideReason || undefined,
        })),
    };

    if (this.editMode && syncId) {
      // Update existing synchronization
      this.synchronizationService
        .updateSynchronization(syncId, submitData)
        .subscribe({
          next: (response) => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.form?.markAsPristine();

            // Show success toast directly
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'synchronization.messages.success.title',
              ),
              detail: this.translateService.instant(
                'synchronization.messages.success.synchronization_updated',
              ),
              life: 4000,
            });

            // Navigate back to list after delay
            setTimeout(
              () => this.router.navigate(['/stock/synchronization']),
              1000,
            );
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error updating synchronization:', error);

            // Show server error message in toast - NO navigation on error
            const errorMessage = extractErrorMessage(error);
            const errorTitle = getErrorTitle(error);
            const errorSeverity = getErrorSeverity(error);

            this.messageService.add({
              severity: errorSeverity,
              summary: errorTitle,
              detail: errorMessage,
              life: 8000,
            });
          },
        });
    } else {
      // Create new synchronization
      this.synchronizationService.createSynchronization(submitData).subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.form?.markAsPristine();
          // Show success toast directly
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'synchronization.messages.success.title',
            ),
            detail: this.translateService.instant(
              'synchronization.messages.success.synchronization_created',
            ),
            life: 4000,
          });

          // Navigate back to list after delay
          setTimeout(
            () => this.router.navigate(['/stock/synchronization']),
            1000,
          );
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error creating synchronization:', error);

          // Show server error message in toast - NO navigation on error
          const errorMessage = extractErrorMessage(error);
          const errorTitle = getErrorTitle(error);
          const errorSeverity = getErrorSeverity(error);

          this.messageService.add({
            severity: errorSeverity,
            summary: errorTitle,
            detail: errorMessage,
            life: 8000,
          });
        },
      });
    }
  }
}
